export type TeamUpdateNotificationSettings = {
  /** Master switch for live alerts. */
  enabled: boolean;
  /** Browser/desktop notifications when the tab is in the background. */
  desktopEnabled: boolean;
  /** Suppress alerts outside the configured work window. */
  muteOutsideWorkHours: boolean;
  /** Local time "HH:mm" inclusive start. */
  workHoursStart: string;
  /** Local time "HH:mm" exclusive end (18:00 means until 17:59). */
  workHoursEnd: string;
  /** When true, weekends are always treated as out of hours. */
  workDaysOnly: boolean;
  /** Suppress reply alerts; still notify for new parent posts. */
  muteReplies: boolean;
  /** Suppress in-app snackbar when already viewing that chat (desktop still allowed if enabled). */
  muteWhenViewingChat: boolean;
  /** Fully mute these chats. */
  mutedGroupIds: string[];
};

export const DEFAULT_TEAM_UPDATE_NOTIFICATION_SETTINGS: TeamUpdateNotificationSettings = {
  enabled: true,
  desktopEnabled: true,
  muteOutsideWorkHours: false,
  workHoursStart: '09:00',
  workHoursEnd: '18:00',
  workDaysOnly: true,
  muteReplies: false,
  muteWhenViewingChat: true,
  mutedGroupIds: [],
};

const STORAGE_KEY = 'overhear.teamUpdates.notificationSettings';
export const TEAM_UPDATE_NOTIFICATION_SETTINGS_EVENT = 'teamUpdatesNotificationSettingsChanged';

function parseTimeToMinutes(value: string): number | null {
  const match = /^([01]?\d|2[0-3]):([0-5]\d)$/.exec(value.trim());
  if (!match) return null;
  return Number(match[1]) * 60 + Number(match[2]);
}

function normaliseSettings(
  partial: Partial<TeamUpdateNotificationSettings> | null | undefined
): TeamUpdateNotificationSettings {
  const merged: TeamUpdateNotificationSettings = {
    ...DEFAULT_TEAM_UPDATE_NOTIFICATION_SETTINGS,
    ...(partial || {}),
    mutedGroupIds: Array.isArray(partial?.mutedGroupIds)
      ? [...new Set(partial.mutedGroupIds.filter(Boolean))]
      : [...DEFAULT_TEAM_UPDATE_NOTIFICATION_SETTINGS.mutedGroupIds],
  };

  if (parseTimeToMinutes(merged.workHoursStart) == null) {
    merged.workHoursStart = DEFAULT_TEAM_UPDATE_NOTIFICATION_SETTINGS.workHoursStart;
  }
  if (parseTimeToMinutes(merged.workHoursEnd) == null) {
    merged.workHoursEnd = DEFAULT_TEAM_UPDATE_NOTIFICATION_SETTINGS.workHoursEnd;
  }

  return merged;
}

export function loadTeamUpdateNotificationSettings(): TeamUpdateNotificationSettings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_TEAM_UPDATE_NOTIFICATION_SETTINGS };
    return normaliseSettings(JSON.parse(raw) as Partial<TeamUpdateNotificationSettings>);
  } catch {
    return { ...DEFAULT_TEAM_UPDATE_NOTIFICATION_SETTINGS };
  }
}

export function saveTeamUpdateNotificationSettings(
  settings: TeamUpdateNotificationSettings
): void {
  const normalised = normaliseSettings(settings);
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(normalised));
  } catch {
    // ignore quota / private mode
  }
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(TEAM_UPDATE_NOTIFICATION_SETTINGS_EVENT));
  }
}

/** True when `now` falls inside the configured work window. */
export function isWithinTeamUpdateWorkHours(
  settings: TeamUpdateNotificationSettings,
  now: Date = new Date()
): boolean {
  if (settings.workDaysOnly) {
    const day = now.getDay(); // 0 Sun … 6 Sat
    if (day === 0 || day === 6) return false;
  }

  const start = parseTimeToMinutes(settings.workHoursStart);
  const end = parseTimeToMinutes(settings.workHoursEnd);
  if (start == null || end == null) return true;

  const minutes = now.getHours() * 60 + now.getMinutes();
  if (start === end) return true; // treat as all-day
  if (start < end) {
    return minutes >= start && minutes < end;
  }
  // Overnight window (e.g. 22:00–06:00)
  return minutes >= start || minutes < end;
}

export type TeamUpdateNotifyDecision = {
  allowInApp: boolean;
  allowDesktop: boolean;
};

export function decideTeamUpdateNotification(
  settings: TeamUpdateNotificationSettings,
  options: {
    kind: 'post' | 'reply';
    groupId: string;
    viewingGroupId?: string | null;
    documentHidden?: boolean;
    now?: Date;
  }
): TeamUpdateNotifyDecision {
  const none = { allowInApp: false, allowDesktop: false };
  if (!settings.enabled) return none;
  if (settings.mutedGroupIds.includes(options.groupId)) return none;
  if (options.kind === 'reply' && settings.muteReplies) return none;
  if (
    settings.muteOutsideWorkHours &&
    !isWithinTeamUpdateWorkHours(settings, options.now || new Date())
  ) {
    return none;
  }

  const viewingThisChat =
    Boolean(options.viewingGroupId) && options.viewingGroupId === options.groupId;
  const hideInAppBecauseViewing =
    settings.muteWhenViewingChat && viewingThisChat && !options.documentHidden;

  return {
    allowInApp: !hideInAppBecauseViewing,
    allowDesktop: settings.desktopEnabled,
  };
}
